/**
 * Bridge: keep PRIVATE comments alive across reloads, without ever putting their
 * text in the shared document body.
 *
 * WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * The docx-native editor stores comments inside the .docx body, and the body is
 * the SAME bytes the counterparty downloads. A private comment sitting there is
 * readable by anyone who opens the file, so `sanitizeExportDocx` strips private
 * threads on the way out — and because save and export shared one buffer, that
 * strip also erased them from what got persisted. Result: private comments
 * vanished on refresh.
 *
 * Making the body keep them is not an option; it IS the leak. So the text has to
 * live somewhere that enforces visibility per viewer — which is exactly what
 * `document_comments` already does (`DocumentCommentsByDocument` filters to
 * public threads plus the requesting user's own private ones, in SQL).
 *
 * WHAT IT DOES ────────────────────────────────────────────────────────────
 * The mirror image of {@link useDocxSuggestionSync}:
 *   1. on each editor change (debounced, alongside autosave) it collects the
 *      comments flagged `isPublic:false`,
 *   2. maps each to a STABLE `commentId` derived from its OOXML `w:id`,
 *   3. `createDocumentComment` for ids not yet persisted.
 * Meanwhile `sanitizeExportDocx(…, 'persist')` reduces each private thread in
 * the body to a content-free `propdocs-pc-<id>` bookmark, and on load
 * `rehydratePrivateCommentsDocx` rebuilds it from whatever the server returns
 * for THIS user. A viewer who isn't entitled to the thread gets an inert
 * bookmark and no text.
 *
 * commentId SHAPE: `docx:comment:{documentId}:{w:id}`.
 * The OOXML `w:id` is unique only within one document, while the column is
 * globally unique — so it is namespaced by document, exactly as suggestion
 * external_ids are. Deterministic, so re-deriving it every sync yields the same
 * id and never duplicates rows.
 *
 * IDEMPOTENT + FAIL-SOFT: creates are skipped for ids already synced this
 * session and swallow a uniqueness error from a prior session. A failed sync
 * never blocks editing or autosave.
 *
 * NOT a general comment mirror: public comments stay in the body, which round
 * trips them fine and keeps them visible in Word. Only private threads — the
 * ones the body must not carry — are mirrored here.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import type { RestorablePrivateComment } from '@propdocs-editor/review';
import { createDocumentComment } from '../../../../utils/api/document-comments/create-comment';
import { getCommentsForLeaseDocumentSection } from '../../../../utils/api/get-comments-for-document-section/get-comments-for-document-section';
import { encodeAuthorToken } from './authorToken';

/** Namespaced, deterministic id for the relational row. */
export function privateCommentExternalId(documentId: string, wId: string | number): string {
  return `docx:comment:${documentId}:${wId}`;
}

/** "Jane Smith" → "JS". Word shows initials on the comment chrome. */
export function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** Recover the OOXML `w:id` from a stored commentId. */
export function wIdFromExternalId(externalId: string): string | null {
  const match = /^docx:comment:[^:]+:(.+)$/.exec(externalId);
  return match ? match[1] : null;
}

/** The shape the editor hands us for each comment. */
interface EditorComment {
  id: string | number;
  content?: unknown;
  isPublic?: boolean;
  parentId?: string | number | null;
  author?: string;
  date?: string;
}

/** Comment content may be rich; reduce to the text we can store and restore. */
export function commentContentToText(content: unknown): string {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) return content.map(commentContentToText).filter(Boolean).join('\n');
  if (typeof content === 'object') {
    const obj = content as Record<string, unknown>;
    if (typeof obj.text === 'string') return obj.text;
    if (Array.isArray(obj.content)) return commentContentToText(obj.content);
  }
  return '';
}

export interface UseDocxPrivateCommentSyncOptions {
  documentId?: string;
  /** Skip entirely for read-only viewers / compare mode. */
  enabled?: boolean;
}

export function useDocxPrivateCommentSync({
  documentId,
  enabled = true,
}: UseDocxPrivateCommentSyncOptions) {
  const client = useApolloClient();
  // ids persisted during THIS session — avoids a mutation per keystroke.
  const syncedRef = useRef<Set<string>>(new Set());
  // OOXML `w:id`s that were RESTORED from the database on load.
  //
  // `isPublic` exists only in the editor's runtime model — OOXML has no such
  // concept, so a rehydrated comment comes back looking public. Left at that,
  // the very next save would skip the persist-mode sanitize and write private
  // text into the body the counterparty downloads. This ref is what
  // `getPrivateCommentIds` uses to keep them classified as private.
  const restoredPrivateIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // A different document means a different id space.
    syncedRef.current = new Set();
    restoredPrivateIdsRef.current = new Set();
  }, [documentId]);

  /**
   * Persist any private comment not yet stored. Safe to call on every change.
   */
  const syncPrivateComments = useCallback(
    async (comments: EditorComment[] | undefined) => {
      if (!enabled || !documentId || !comments?.length) return;

      // A comment is private if the composer flagged it OR it was restored from
      // the private mirror on load (see `restoredPrivateIdsRef`). Without the
      // second clause an EDIT to a restored comment would never be persisted.
      const privateComments = comments.filter(
        (c) => c.isPublic === false || restoredPrivateIdsRef.current.has(String(c.id))
      );
      if (!privateComments.length) return;

      for (const comment of privateComments) {
        const externalId = privateCommentExternalId(documentId, String(comment.id));

        const text = commentContentToText(comment.content);
        if (!text) continue; // nothing to preserve

        // Keyed on id AND text, not id alone: a restored comment must not be
        // re-created row-for-row on every autosave, while an EDIT (same id, new
        // text) still gets a fresh row — reads take the newest row per w:id.
        const stamp = `${externalId} ${text}`;
        if (syncedRef.current.has(stamp)) continue;

        // Mark BEFORE awaiting: a second change firing mid-flight must not
        // issue a duplicate create for the same id.
        syncedRef.current.add(stamp);

        try {
          await createDocumentComment({
            documentId,
            comment: {
              commentId: externalId,
              content: text,
              isPublic: false,
              threadId: externalId,
            },
          })(client);
        } catch {
          // Already exists from a previous session (uniqueness), or the network
          // blipped. Either way this must never interrupt editing; the next
          // load re-reads from the server regardless.
        }
      }
    },
    [client, documentId, enabled]
  );

  /**
   * Private comments the SERVER says this user may see, shaped for
   * `rehydratePrivateCommentsDocx`.
   *
   * Authorization is entirely the server's: this returns whatever
   * `DocumentCommentsByDocument` yields, which is already filtered to the
   * caller's own private threads. It is never a full-table read.
   */
  const fetchRestorablePrivateComments = useCallback(async (): Promise<RestorablePrivateComment[]> => {
    if (!documentId) return [];

    try {
      // `getDocumentCommentsByDocument` is declared `CkeDocumentComments!` — a
      // SINGLE thread object, NOT a list. Iterating it with `for…of` threw
      // "is not iterable", the catch below swallowed it, and restoration
      // silently returned nothing on EVERY load. Normalize to a list instead of
      // assuming either shape.
      const payload = await getCommentsForLeaseDocumentSection(
        documentId,
        '',
        'network-only'
      )(client);
      const threads = Array.isArray(payload) ? payload : payload ? [payload] : [];
      const restorable: RestorablePrivateComment[] = [];

      // LAST row per w:id wins. Rows are ordered by id ascending, and a comment
      // that was edited (or re-created after an id collision) yields several
      // rows sharing one external_id — the newest is the current text.
      const byWId = new Map<string, RestorablePrivateComment>();
      // w:id → its thread, so replies can be linked to their root below.
      const threadOfWId = new Map<string, string>();
      // Thread → the FIRST w:id seen in it (rows arrive in id order, so that's
      // the root of the conversation).
      const rootOfThread = new Map<string, string>();

      for (const thread of threads ?? []) {
        for (const c of thread?.comments ?? []) {
          if (c?.isPublic !== false) continue; // public comments live in the body
          const wId = wIdFromExternalId(String(c.commentId ?? ''));
          if (!wId) continue; // not one of ours (legacy CKE row)
          // `authorName` comes from the row's user. Without it the rebuilt
          // definition fell back to the literal 'PropDocs', so every restored
          // private comment was attributed to a placeholder instead of the
          // person who wrote it.
          const displayName = (c.authorName as string | null | undefined) || undefined;
          const authorId = c.authorId ? String(c.authorId) : undefined;
          // Rebuild the SAME author value a live comment carries — the id token,
          // not the bare name. The avatar colour keys on the id (see
          // `authorIdentityKey`), so a restored comment written as a plain name
          // landed on a different colour than the one the author had a moment
          // earlier, which is the "colour changes on reload" report.
          const author = displayName
            ? encodeAuthorToken({ authorId, display: displayName })
            : undefined;
          byWId.set(wId, {
            id: wId,
            text: String(c.content ?? ''),
            author,
            initials: displayName ? initialsFor(displayName) : undefined,
            date: c.createdAt ? new Date(c.createdAt).toISOString() : undefined,
          });

          // THREADING. `threadId` is the row's `section_name`. A comment written
          // in the DOCX editor is its own thread (the id is per-comment), so
          // nothing links. A thread MIGRATED from CKEditor keeps its original
          // section_name across the root and every reply, which is the only
          // record of the conversation once each row has its own anchor —
          // without this a migrated 5-message thread came back as 5 unrelated
          // cards.
          const threadKey = String(c.threadId ?? '');
          if (threadKey) {
            threadOfWId.set(wId, threadKey);
            if (!rootOfThread.has(threadKey)) rootOfThread.set(threadKey, wId);
          }
        }
      }

      for (const entry of byWId.values()) {
        const threadKey = threadOfWId.get(entry.id);
        const root = threadKey ? rootOfThread.get(threadKey) : undefined;
        if (root && root !== entry.id) entry.parentId = root;
      }
      restorable.push(...byWId.values());

      // Remember which w:ids are private. The editor's model can't tell — see
      // `restoredPrivateIdsRef` — and `getPrivateCommentIds` is what keeps the
      // next save stripping them out of the shared body.
      for (const c of restorable) restoredPrivateIdsRef.current.add(String(c.id));
      // Already in the mirror; don't re-create a row for unchanged text.
      for (const c of restorable) {
        syncedRef.current.add(`${privateCommentExternalId(documentId, c.id)} ${c.text}`);
      }

      return restorable;
    } catch (err) {
      // Never block opening the document — the comments simply stay collapsed
      // into their inert bookmarks for this session. LOUD in dev: this catch
      // once hid a plain TypeError for weeks, and the symptom ("private
      // comments vanish on reload") is indistinguishable from a storage bug.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[private-comments] restore failed', err);
      }
      return [];
    }
  }, [client, documentId]);

  /**
   * SECURITY: the ids the save path must strip from the persisted body.
   *
   * Passed to the editor as `getPrivateCommentIds`. Its own fallback —
   * `comments.filter(c => c.isPublic === false)` — is blind to RESTORED
   * comments, because `isPublic` is a runtime-only flag that no round trip
   * through OOXML can carry. Union of both so a private comment stays private
   * whether it was just written or rebuilt from the mirror.
   */
  const getPrivateCommentIds = useCallback(
    (comments?: EditorComment[]): string[] => {
      const ids = new Set(restoredPrivateIdsRef.current);
      for (const c of comments ?? []) {
        if (c.isPublic === false) ids.add(String(c.id));
      }
      return [...ids];
    },
    []
  );

  return { syncPrivateComments, fetchRestorablePrivateComments, getPrivateCommentIds };
}
